import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Search,
  Package,
  MapPin,
  User,
  Calendar,
  CheckCircle,
  Clock,
  Truck,
  AlertCircle,
  XCircle,
  Crown,
  ShieldCheck,
  Mail,
  Loader2,
  RefreshCw,
} from 'lucide-react';
import { useLocation } from 'react-router-dom';
import Header from '@/components/layout/Header';
import FooterNew from '@/components/layout/FooterNew';
import DeliveryEstimateSummary from '@/components/shared/DeliveryEstimateSummary';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { API } from '@/App';

const INITIAL_EMAIL_STATE = {
  loading: false,
  eligible: false,
  reason: '',
  subscription_active: false,
  subscription_status: null,
  subscription_confirmed: false,
  requires_confirmation: false,
  confirmation_expired: false,
  can_subscribe: false,
  can_resend_confirmation: false,
  public_message: '',
};

const STATUS_CONFIG = {
  pending_payment: {
    label: 'En attente de paiement',
    icon: Clock,
    color: 'text-amber-600',
    bgColor: 'bg-amber-100',
    borderColor: 'border-amber-300',
  },
  paid: {
    label: 'Payé',
    icon: CheckCircle,
    color: 'text-green-600',
    bgColor: 'bg-green-100',
    borderColor: 'border-green-300',
  },
  awaiting_assignment: {
    label: "En attente d'assignation",
    icon: Clock,
    color: 'text-orange-600',
    bgColor: 'bg-orange-100',
    borderColor: 'border-orange-300',
  },
  assigned: {
    label: 'Livreur assigné',
    icon: Truck,
    color: 'text-cyan-700',
    bgColor: 'bg-cyan-100',
    borderColor: 'border-cyan-300',
  },
  pickup_in_progress: {
    label: 'Ramassage en cours',
    icon: Package,
    color: 'text-blue-600',
    bgColor: 'bg-blue-100',
    borderColor: 'border-blue-300',
  },
  picked_up: {
    label: 'Colis pris en charge',
    icon: CheckCircle,
    color: 'text-green-600',
    bgColor: 'bg-green-100',
    borderColor: 'border-green-300',
  },
  in_transit: {
    label: "En cours d'acheminement",
    icon: Truck,
    color: 'text-purple-600',
    bgColor: 'bg-purple-100',
    borderColor: 'border-purple-300',
  },
  at_relay: {
    label: 'Au point relais',
    icon: MapPin,
    color: 'text-cyan-600',
    bgColor: 'bg-cyan-100',
    borderColor: 'border-cyan-300',
  },
  transferred: {
    label: 'Transfert logistique',
    icon: Truck,
    color: 'text-indigo-600',
    bgColor: 'bg-indigo-100',
    borderColor: 'border-indigo-300',
  },
  out_for_delivery: {
    label: 'En cours de livraison',
    icon: Truck,
    color: 'text-blue-600',
    bgColor: 'bg-blue-100',
    borderColor: 'border-blue-300',
  },
  delivered: {
    label: 'Livré',
    icon: CheckCircle,
    color: 'text-green-600',
    bgColor: 'bg-green-100',
    borderColor: 'border-green-300',
  },
  delivered_with_proof: {
    label: 'Livré',
    icon: CheckCircle,
    color: 'text-green-600',
    bgColor: 'bg-green-100',
    borderColor: 'border-green-300',
  },
  client_absent: {
    label: 'Client absent',
    icon: AlertCircle,
    color: 'text-amber-700',
    bgColor: 'bg-amber-100',
    borderColor: 'border-amber-300',
  },
  delivery_rescheduled: {
    label: 'Livraison reprogrammée',
    icon: Calendar,
    color: 'text-orange-700',
    bgColor: 'bg-orange-100',
    borderColor: 'border-orange-300',
  },
  delivery_failed: {
    label: 'Échec de livraison',
    icon: XCircle,
    color: 'text-red-600',
    bgColor: 'bg-red-100',
    borderColor: 'border-red-300',
  },
  cancelled: {
    label: 'Annulé',
    icon: XCircle,
    color: 'text-red-600',
    bgColor: 'bg-red-100',
    borderColor: 'border-red-300',
  },
};

const normalizeTrackingCode = (value) => String(value || '').trim().toUpperCase();

const normalizeEmailState = (payload = {}) => ({
  loading: false,
  eligible: Boolean(payload.eligible),
  reason: payload.reason || '',
  subscription_active: Boolean(payload.subscription_active),
  subscription_status: payload.subscription_status || null,
  subscription_confirmed: Boolean(payload.subscription_confirmed),
  requires_confirmation: Boolean(payload.requires_confirmation),
  confirmation_expired: Boolean(payload.confirmation_expired),
  can_subscribe: Boolean(payload.can_subscribe),
  can_resend_confirmation: Boolean(payload.can_resend_confirmation),
  public_message: payload.public_message || '',
});

const isSingleValidEmail = (value) => {
  const candidate = String(value || '').trim().toLowerCase();
  if (!candidate || /[,;\n\r\t]/.test(candidate)) {
    return false;
  }
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(candidate);
};

const TrackingPageNew = () => {
  const location = useLocation();
  const [trackingCode, setTrackingCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [delivery, setDelivery] = useState(null);
  const [error, setError] = useState('');
  const [trackingEmail, setTrackingEmail] = useState(INITIAL_EMAIL_STATE);
  const [trackingEmailInput, setTrackingEmailInput] = useState('');
  const [trackingEmailError, setTrackingEmailError] = useState('');
  const [trackingEmailSubmitting, setTrackingEmailSubmitting] = useState(false);
  const [trackingEmailResending, setTrackingEmailResending] = useState(false);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getCurrentStatusConfig = () => {
    if (!delivery) return null;
    return STATUS_CONFIG[delivery.status] || STATUS_CONFIG.in_transit;
  };

  const getServiceTier = (item) => {
    if (!item) return 'standard';
    if (item.service_tier) return item.service_tier;
    return item.is_express ? 'express' : 'standard';
  };

  const getDeliveryOtpState = () => {
    const confirmation = delivery?.delivery_confirmation || {};
    const otp = confirmation.otp || {};
    return {
      otp_required: Boolean(confirmation.otp_required),
      otp_verified: Boolean(confirmation.otp_verified),
      tracking_email_confirmed: Boolean(confirmation.tracking_email_confirmed),
      fallback_reason: confirmation.fallback_reason || '',
      otp_status: String(otp.status || '').trim().toLowerCase(),
    };
  };

  const syncQueryString = (nextTrackingCode) => {
    if (typeof window === 'undefined' || !window.history?.replaceState) {
      return;
    }
    const normalizedTrackingCode = normalizeTrackingCode(nextTrackingCode);
    const nextUrl = normalizedTrackingCode
      ? `${location.pathname}?code=${encodeURIComponent(normalizedTrackingCode)}`
      : location.pathname;
    window.history.replaceState(null, '', nextUrl);
  };

  const fetchTrackingEmailState = async (deliveryPayload, trackingReference) => {
    const reference = deliveryPayload?.tracking_code || normalizeTrackingCode(trackingReference);
    if (!reference) {
      setTrackingEmail(INITIAL_EMAIL_STATE);
      return;
    }

    setTrackingEmail({
      ...normalizeEmailState(deliveryPayload?.email_tracking),
      loading: true,
    });

    try {
      const response = await axios.get(`${API}/track/${reference}/email-subscription-status`);
      setTrackingEmail({
        ...normalizeEmailState(response.data),
        loading: false,
      });
    } catch (_statusError) {
      setTrackingEmail({
        ...normalizeEmailState(deliveryPayload?.email_tracking),
        loading: false,
      });
    }
  };

  const loadTracking = async (nextTrackingCode) => {
    const normalizedTrackingCode = normalizeTrackingCode(nextTrackingCode);
    if (!normalizedTrackingCode) {
      return;
    }

    setLoading(true);
    setError('');
    setDelivery(null);
    setTrackingEmail(INITIAL_EMAIL_STATE);
    setTrackingEmailInput('');
    setTrackingEmailError('');

    try {
      const response = await axios.get(`${API}/track/${normalizedTrackingCode}`);
      setDelivery(response.data);
      setTrackingCode(normalizedTrackingCode);
      syncQueryString(normalizedTrackingCode);
      await fetchTrackingEmailState(response.data, normalizedTrackingCode);
    } catch (err) {
      setTrackingEmail(INITIAL_EMAIL_STATE);
      if (err.response?.status === 404) {
        setError('Code de suivi invalide. Veuillez vérifier et réessayer.');
      } else {
        setError('Erreur lors de la récupération des informations. Veuillez réessayer.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (event) => {
    event.preventDefault();
    await loadTracking(trackingCode);
  };

  const handleSubscribeTrackingEmail = async () => {
    const normalizedEmail = String(trackingEmailInput || '').trim().toLowerCase();
    if (!delivery?.tracking_code || !trackingEmail.can_subscribe) {
      return;
    }
    if (!isSingleValidEmail(normalizedEmail)) {
      setTrackingEmailError('Veuillez saisir une seule adresse email valide.');
      return;
    }

    setTrackingEmailSubmitting(true);
    setTrackingEmailError('');
    try {
      const response = await axios.post(
        `${API}/track/${delivery.tracking_code}/subscribe-email`,
        { email: normalizedEmail }
      );
      setTrackingEmail({
        ...normalizeEmailState(response.data?.data || {}),
        loading: false,
      });
      setTrackingEmailInput('');
    } catch (err) {
      const detail = err.response?.data?.detail || '';
      if (detail === 'MULTIPLE_EMAILS_NOT_ALLOWED') {
        setTrackingEmailError('Une seule adresse email est autorisée pour ce colis.');
      } else if (detail === 'INVALID_EMAIL') {
        setTrackingEmailError('Veuillez saisir une adresse email valide.');
      } else {
        setTrackingEmailError(
          err.response?.data?.message
            || err.response?.data?.detail
            || "Impossible d'activer le suivi email pour le moment."
        );
      }
    } finally {
      setTrackingEmailSubmitting(false);
    }
  };

  const handleResendTrackingEmailConfirmation = async () => {
    if (!delivery?.tracking_code || !trackingEmail.can_resend_confirmation) {
      return;
    }

    setTrackingEmailResending(true);
    setTrackingEmailError('');
    try {
      const response = await axios.post(`${API}/tracking/email/resend-confirmation`, {
        tracking_code: delivery.tracking_code,
      });
      setTrackingEmail({
        ...normalizeEmailState(response.data?.data || {}),
        loading: false,
      });
    } catch (err) {
      setTrackingEmailError(
        err.response?.data?.detail
          || err.response?.data?.message
          || "Impossible de renvoyer l'email de confirmation pour le moment."
      );
    } finally {
      setTrackingEmailResending(false);
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search || '');
    const codeFromQuery = normalizeTrackingCode(params.get('code'));
    if (!codeFromQuery) {
      return;
    }
    setTrackingCode(codeFromQuery);
    loadTracking(codeFromQuery);
  }, [location.search]);

  return (
    <div className="flex min-h-screen flex-col">
      <Header />

      <main className="flex-1">
        <section className="bg-gradient-to-br from-red-50 to-cyan-50 py-16">
          <div className="container mx-auto px-4">
            <div className="mx-auto max-w-3xl text-center">
              <Package className="mx-auto mb-6 h-16 w-16 text-red-600" />
              <h1 className="mb-6 text-4xl font-bold gradient-text md:text-5xl">
                Suivez votre colis en temps réel
              </h1>
              <p className="mb-8 text-lg text-gray-700">
                Entrez votre code de suivi pour voir où se trouve votre colis.
              </p>

              <form onSubmit={handleSearch} className="mx-auto max-w-2xl">
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
                    <Input
                      type="text"
                      placeholder="Entrez votre code de suivi (ex: CN1234567890)"
                      value={trackingCode}
                      onChange={(event) => setTrackingCode(normalizeTrackingCode(event.target.value))}
                      className="h-14 pl-10 text-lg"
                      disabled={loading}
                      data-testid="public-tracking-code-input"
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={loading || !trackingCode.trim()}
                    className="h-14 px-8 bg-gradient-to-r from-red-500 to-cyan-500 hover:from-red-600 hover:to-cyan-600"
                    data-testid="public-track-submit"
                  >
                    {loading ? 'Recherche...' : 'Suivre'}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </section>

        {error && (
          <section className="py-8">
            <div className="container mx-auto px-4">
              <div className="mx-auto max-w-2xl">
                <Card className="border-2 border-red-200 bg-red-50">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-3">
                      <AlertCircle className="h-6 w-6 flex-shrink-0 text-red-600" />
                      <div>
                        <p className="mb-1 font-semibold text-red-900">Code de suivi introuvable</p>
                        <p className="text-red-700">{error}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </section>
        )}

        {delivery && (
          <section className="py-12">
            <div className="container mx-auto px-4">
              <div className="mx-auto max-w-5xl space-y-6">
                <Card className={`border-2 ${getCurrentStatusConfig()?.borderColor}`}>
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between gap-4">
                      <div className="flex items-center gap-4">
                        {React.createElement(getCurrentStatusConfig()?.icon, {
                          className: `h-12 w-12 ${getCurrentStatusConfig()?.color}`,
                        })}
                        <div>
                          <p className="text-sm text-gray-600">Statut actuel</p>
                          <p className="text-2xl font-bold text-gray-900">
                            {getCurrentStatusConfig()?.label}
                          </p>
                        </div>
                      </div>

                      {(() => {
                        const serviceTier = getServiceTier(delivery);
                        const isExpressTier = serviceTier === 'express';
                        const isInsured = Boolean(delivery.is_insured);
                        return (
                          <div className="flex flex-wrap items-center justify-end gap-2">
                            {!isExpressTier && <Badge variant="outline">Service Standard</Badge>}
                            {isExpressTier && (
                              <Badge className="flex items-center gap-1 bg-gradient-to-r from-amber-400 to-orange-500 px-4 py-2 text-lg text-white">
                                <Crown className="h-4 w-4" />
                                EXPRESS
                              </Badge>
                            )}
                            {isInsured && (
                              <Badge className="flex items-center gap-1 bg-cyan-600 px-4 py-2 text-lg text-white">
                                <ShieldCheck className="h-4 w-4" />
                                Assurance
                              </Badge>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                  </CardContent>
                </Card>

                <DeliveryEstimateSummary
                  source={delivery}
                  title="Délai estimé"
                  testId="tracking-delivery-estimate"
                />

                {(() => {
                  const otpState = getDeliveryOtpState();
                  const shouldRenderOtpCard = Boolean(
                    otpState.otp_required || otpState.otp_verified || otpState.fallback_reason
                  );

                  if (!shouldRenderOtpCard) {
                    return null;
                  }

                  let title = 'Remise avec code OTP';
                  let message = otpState.fallback_reason || 'Le backend pilote le mode de validation final de cette livraison.';
                  let toneClasses = 'border-cyan-200 bg-cyan-50 text-cyan-900';

                  if (otpState.otp_verified) {
                    title = 'Code de remise validé';
                    message = 'Le code client a déjà été validé pour cette livraison.';
                    toneClasses = 'border-emerald-200 bg-emerald-50 text-emerald-900';
                  } else if (otpState.otp_required && otpState.otp_status === 'pending') {
                    title = 'Code de remise actif';
                    message = otpState.tracking_email_confirmed
                      ? 'Un code de remise a été envoyé par email. Donnez-le au livreur au moment de la remise.'
                      : 'Le code de remise sera activé dès qu un email de suivi confirmé sera disponible.';
                  } else if (otpState.otp_required && otpState.otp_status === 'expired') {
                    title = 'Code de remise expiré';
                    message = 'Le livreur pourra demander un nouveau code si la livraison est toujours en cours.';
                    toneClasses = 'border-amber-200 bg-amber-50 text-amber-900';
                  } else if (otpState.otp_required && otpState.otp_status === 'locked') {
                    title = 'Code de remise bloqué';
                    message = 'Le code de remise est temporairement bloqué après trop de tentatives.';
                    toneClasses = 'border-red-200 bg-red-50 text-red-900';
                  }

                  return (
                    <Card data-testid="tracking-delivery-otp-card" className={toneClasses}>
                      <CardContent className="p-5">
                        <div className="flex items-start gap-3">
                          <ShieldCheck className="mt-0.5 h-5 w-5 flex-shrink-0" />
                          <div>
                            <p className="font-semibold">{title}</p>
                            <p className="mt-1 text-sm opacity-90">{message}</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })()}

                <Card data-testid="tracking-email-card">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Mail className="h-5 w-5 text-cyan-600" />
                      Recevoir les mises à jour par email
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {trackingEmail.loading ? (
                      <div className="flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">
                        <Loader2 className="h-4 w-4 animate-spin text-cyan-600" />
                        Vérification de la disponibilité du suivi email...
                      </div>
                    ) : trackingEmail.requires_confirmation ? (
                      <div
                        data-testid="tracking-email-pending-state"
                        className="rounded-xl border border-amber-200 bg-amber-50 p-4"
                      >
                        <div className="flex items-start gap-3">
                          <Mail className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-700" />
                          <div className="space-y-3">
                            <div>
                              <p className="font-semibold text-amber-900">
                                Confirmation email en attente
                              </p>
                              <p className="mt-1 text-sm text-amber-800">
                                {trackingEmail.public_message || 'Un email de confirmation a ete envoye pour activer le suivi de ce colis.'}
                              </p>
                            </div>
                            <p className="text-sm text-amber-800">
                              {trackingEmail.confirmation_expired
                                ? 'Le dernier lien a expire. Vous pouvez demander un nouvel email de confirmation.'
                                : "Ouvrez l'email recu puis cliquez sur le lien securise pour activer definitivement les notifications."}
                            </p>
                            {trackingEmail.can_resend_confirmation ? (
                              <Button
                                type="button"
                                variant="outline"
                                className="border-amber-300 bg-white text-amber-900 hover:bg-amber-100"
                                data-testid="tracking-email-resend"
                                disabled={trackingEmailResending}
                                onClick={handleResendTrackingEmailConfirmation}
                              >
                                <RefreshCw className={`mr-2 h-4 w-4 ${trackingEmailResending ? 'animate-spin' : ''}`} />
                                {trackingEmailResending ? 'Envoi...' : 'Renvoyer email'}
                              </Button>
                            ) : null}
                            {trackingEmailError ? (
                              <p className="text-sm text-red-600">{trackingEmailError}</p>
                            ) : null}
                          </div>
                        </div>
                      </div>
                    ) : trackingEmail.subscription_confirmed || trackingEmail.subscription_active ? (
                      <div
                        data-testid="tracking-email-active-state"
                        className="rounded-xl border border-emerald-200 bg-emerald-50 p-4"
                      >
                        <div className="flex items-start gap-3">
                          <CheckCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-emerald-600" />
                          <div>
                            <p className="font-semibold text-emerald-900">
                              Le suivi email est déjà activé pour ce colis.
                            </p>
                            <p className="mt-1 text-sm text-emerald-800">
                              Les prochaines mises à jour utiles seront envoyées automatiquement.
                            </p>
                          </div>
                        </div>
                      </div>
                    ) : trackingEmail.eligible ? (
                      <>
                        <div className="rounded-xl border border-cyan-200 bg-cyan-50 p-4 text-sm text-cyan-900">
                          <p className="font-semibold">
                            {trackingEmail.public_message || 'Recevez les prochaines étapes utiles de ce colis par email.'}
                          </p>
                          <p className="mt-1 text-cyan-800">
                            Une seule adresse email peut être active pour ce colis.
                          </p>
                        </div>

                        <div className="grid gap-3 sm:grid-cols-[1fr_auto]">
                          <div>
                            <Input
                              type="email"
                              value={trackingEmailInput}
                              onChange={(event) => setTrackingEmailInput(event.target.value.toLowerCase())}
                              placeholder="Votre adresse email"
                              data-testid="tracking-email-input"
                              disabled={trackingEmailSubmitting}
                            />
                            {trackingEmailError && (
                              <p className="mt-2 text-sm text-red-600">{trackingEmailError}</p>
                            )}
                          </div>
                          <Button
                            type="button"
                            className="bg-cyan-600 hover:bg-cyan-700"
                            data-testid="tracking-email-submit"
                            disabled={trackingEmailSubmitting}
                            onClick={handleSubscribeTrackingEmail}
                          >
                            {trackingEmailSubmitting ? 'Activation...' : 'Activer le suivi email'}
                          </Button>
                        </div>
                      </>
                    ) : (
                      <div
                        data-testid="tracking-email-disabled-state"
                        className="rounded-xl border border-slate-200 bg-slate-50 p-4"
                      >
                        <div className="flex items-start gap-3">
                          <AlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-slate-500" />
                          <div>
                            <p className="font-semibold text-slate-900">Suivi email indisponible</p>
                            <p className="mt-1 text-sm text-slate-600">
                              {trackingEmail.reason || "Le suivi email n'est pas disponible pour ce colis."}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>

                <div className="grid gap-6 md:grid-cols-2">
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Package className="h-5 w-5 text-red-600" />
                        Informations du colis
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div>
                        <p className="text-sm text-gray-600">Code de suivi</p>
                        <p className="text-lg font-semibold">{delivery.tracking_code}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Poids</p>
                        <p className="font-semibold">{delivery.weight} kg</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Description</p>
                        <p className="font-semibold">{delivery.description}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Coût</p>
                        <p className="text-lg font-semibold">
                          {delivery.cost} {delivery.currency}
                        </p>
                        {(delivery.transport_amount !== undefined
                          || delivery.express_surcharge_amount
                          || delivery.insurance_amount) && (
                          <div className="mt-2 border-t pt-2 text-xs text-gray-600">
                            <p>Transport: {Number(delivery.transport_amount ?? delivery.cost ?? 0).toLocaleString()} {delivery.currency}</p>
                            <p>Express: + {Number(delivery.express_surcharge_amount ?? 0).toLocaleString()} {delivery.currency}</p>
                            <p>Assurance: + {Number(delivery.insurance_amount ?? 0).toLocaleString()} {delivery.currency}</p>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <MapPin className="h-5 w-5 text-cyan-600" />
                        Adresses
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div>
                        <p className="mb-1 text-sm text-gray-600">Ramassage</p>
                        <p className="font-semibold">{delivery.pickup_address}</p>
                        <p className="text-sm text-gray-600">
                          {delivery.pickup_district}, {delivery.pickup_city}
                        </p>
                      </div>
                      <div className="border-t pt-3">
                        <p className="mb-1 text-sm text-gray-600">Livraison</p>
                        <p className="font-semibold">{delivery.delivery_address}</p>
                        <p className="text-sm text-gray-600">
                          {delivery.delivery_district}, {delivery.delivery_city}
                        </p>
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <User className="h-5 w-5 text-green-600" />
                        Destinataire
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div>
                        <p className="text-sm text-gray-600">Nom</p>
                        <p className="font-semibold">{delivery.client_info?.full_name}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Téléphone</p>
                        <p className="font-semibold">{delivery.client_info?.phone}</p>
                      </div>
                    </CardContent>
                  </Card>

                  {delivery.livreur_name && (
                    <Card>
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <Truck className="h-5 w-5 text-blue-600" />
                          Livreur assigné
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        <div>
                          <p className="text-sm text-gray-600">Nom du livreur</p>
                          <p className="font-semibold">{delivery.livreur_name}</p>
                        </div>
                        {delivery.current_location && (
                          <div>
                            <p className="text-sm text-gray-600">Position actuelle</p>
                            <p className="font-semibold text-green-600">Disponible</p>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  )}
                </div>

                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Calendar className="h-5 w-5 text-purple-600" />
                      Historique de suivi
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="relative">
                      <div className="absolute bottom-0 left-6 top-0 w-0.5 bg-gray-200" />

                      <div className="space-y-6">
                        {delivery.tracking_history?.map((event, index) => {
                          const config = STATUS_CONFIG[event.status] || STATUS_CONFIG.in_transit;
                          const Icon = config.icon;
                          const isLast = index === delivery.tracking_history.length - 1;

                          return (
                            <div key={`${event.status}-${event.timestamp || index}`} className="relative flex gap-4">
                              <div
                                className={`relative z-10 flex h-12 w-12 items-center justify-center rounded-full border-2 ${config.bgColor} ${config.borderColor}`}
                              >
                                <Icon className={`h-6 w-6 ${config.color}`} />
                              </div>

                              <div className={`flex-1 pb-6 ${!isLast ? 'border-b' : ''}`}>
                                <div className="flex items-start justify-between gap-4">
                                  <div>
                                    <p className="font-semibold text-gray-900">{config.label}</p>
                                    {event.note && (
                                      <p className="mt-1 text-sm text-gray-600">{event.note}</p>
                                    )}
                                  </div>
                                  <p className="text-sm text-gray-500">{formatDate(event.timestamp)}</p>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </section>
        )}

        {!delivery && !error && (
          <section className="py-12">
            <div className="container mx-auto px-4">
              <div className="mx-auto max-w-4xl">
                <h2 className="mb-8 text-center text-2xl font-bold text-gray-900">
                  Comment ça marche ?
                </h2>
                <div className="grid gap-6 md:grid-cols-3">
                  <Card className="text-center">
                    <CardContent className="pt-6">
                      <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-red-100">
                        <span className="text-2xl font-bold text-red-600">1</span>
                      </div>
                      <h3 className="mb-2 font-semibold">Récupérez votre code</h3>
                      <p className="text-sm text-gray-600">
                        Votre code de suivi vous est communiqué dès validation du paiement.
                      </p>
                    </CardContent>
                  </Card>

                  <Card className="text-center">
                    <CardContent className="pt-6">
                      <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-cyan-100">
                        <span className="text-2xl font-bold text-cyan-600">2</span>
                      </div>
                      <h3 className="mb-2 font-semibold">Entrez le code</h3>
                      <p className="text-sm text-gray-600">
                        Saisissez votre code dans le champ de recherche ci-dessus.
                      </p>
                    </CardContent>
                  </Card>

                  <Card className="text-center">
                    <CardContent className="pt-6">
                      <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
                        <span className="text-2xl font-bold text-green-600">3</span>
                      </div>
                      <h3 className="mb-2 font-semibold">Suivez en temps réel</h3>
                      <p className="text-sm text-gray-600">
                        Consultez l'état de votre colis et activez les mises à jour utiles par email.
                      </p>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </div>
          </section>
        )}
      </main>

      <FooterNew />
    </div>
  );
};

export default TrackingPageNew;
